import React, { useEffect, useRef, useState } from 'react';
import { Controller, useController, useFormContext } from 'react-hook-form';
import {
  Box,
  ContentLabelField,
  ContentList,
  DateRangePicker,
  Grid,
  Option,
  Text,
} from '@hopskipdrive/react-components';
import { FormHelperText } from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import { CalendarDropdown } from '@rideiq/shared/components';
import { colorPalette, colorTokens } from '@rideiq/shared/components/variables';
import { addDays, isBefore, isEqual, isPast, isSameDay, isValid, startOfDay, subDays } from 'date-fns';
import { compact } from 'lodash';

import { DateSectionContainer, seriesDayOptionStyles } from './DateSection.styles';
import { SeriesDateRangeTextField } from './SeriesDateRangeTextField';

interface DateSectionForSeriesProps {
  nextAvailableDate: Date;
  initialDays: DaysOfTheWeek;
  rideDetailsDefault: RideFormType;
  isLocationSelected: boolean;
}

export const DateSectionForSeries: React.FC<DateSectionForSeriesProps> = ({
  nextAvailableDate,
  initialDays,
  rideDetailsDefault,
  isLocationSelected,
}) => {
  const [days, setDays] = useState(initialDays);
  const {
    setValue,
    control,
    register,
    clearErrors,
    formState: { errors },
  } = useFormContext<RideFormData>();

  const { field: startDateField } = useController({
    control,
    name: 'tripTemplateAttributes.startDate',
    defaultValue: rideDetailsDefault.tripTemplateAttributes?.startDate,
  });
  const { field: endDateField } = useController({
    control,
    name: 'tripTemplateAttributes.endDate',
    defaultValue: rideDetailsDefault.tripTemplateAttributes?.endDate,
  });
  const startDateDisabled =
    window.location.pathname !== '/rides/duplicate' &&
    !!rideDetailsDefault.tripTemplateAttributes?.startDate &&
    isPast(rideDetailsDefault.tripTemplateAttributes.startDate);
  const [rangePosition, setRangePosition] = useState<'start' | 'end'>(startDateDisabled ? 'end' : 'start');
  const keyboardOpenRequested = useRef(false);
  const formFieldRef = useForkRef(startDateField.ref, endDateField.ref);
  const dateError = [errors.tripTemplateAttributes?.startDate?.message, errors.tripTemplateAttributes?.endDate?.message]
    .filter(Boolean)
    .join(' ');
  const dateRangeTextFieldProps = {
    id: 'series-date-range',
    error: !!dateError,
    helperText:
      dateError || (startDateDisabled ? 'The start date is fixed because this series has already started.' : undefined),
    startLocked: startDateDisabled,
    formFieldRef,
    onBlur: () => {
      startDateField.onBlur();
      endDateField.onBlur();
    },
    inputProps: { 'data-testid': 'series-date-range' },
  };
  const minEndDate =
    startDateField.value && startDateField.value >= nextAvailableDate
      ? addDays(startDateField.value, 1)
      : addDays(nextAvailableDate, 1);

  useEffect(() => {
    if (errors.tripTemplateAttributes?.repeats && compact(Object.values(days)).length > 0) {
      clearErrors('tripTemplateAttributes.repeats');
    }
  }, [days, errors]);

  return (
    <DateSectionContainer mb={4} id="content-panel">
      <Grid container>
        <Grid item xs={12} md={12} lg={12}>
          <Text variant="h3" fontWeight={500}>
            Ride Series Schedule
          </Text>
          <ContentList>
            <Box display="flex" flexDirection="column" flexGrow={1} mt={4}>
              <>
                <ContentLabelField
                  id="days"
                  title="Repeats"
                  editMode={true}
                  editView={
                    <Box bgcolor={colorTokens.background.default} p={4.5} pl={0}>
                      <Box
                        border={1}
                        borderColor={
                          errors.tripTemplateAttributes?.repeats ? colorPalette.red45 : colorTokens.text.contrast
                        }
                        borderRadius={4}
                        padding={2}
                        paddingRight={1}
                      >
                        <Option
                          sx={seriesDayOptionStyles}
                          aria-label="Monday"
                          mr={1}
                          label="Mon"
                          data-testid="monday"
                          active={days.monday}
                          aria-pressed={days.monday}
                          onClick={() => {
                            setDays((prevState) => ({
                              ...prevState,
                              monday: !prevState.monday,
                            }));
                            setValue('tripTemplateAttributes.monday', !days.monday);
                          }}
                          onChange={() => setValue('tripTemplateAttributes.monday', !days.monday)}
                        />
                        <Option
                          sx={seriesDayOptionStyles}
                          aria-label="Tuesday"
                          mr={1}
                          label="Tue"
                          data-testid="tuesday"
                          active={days.tuesday}
                          aria-pressed={days.tuesday}
                          onClick={() => {
                            setDays((prevState) => ({
                              ...prevState,
                              tuesday: !prevState.tuesday,
                            }));
                            setValue('tripTemplateAttributes.tuesday', !days.tuesday);
                          }}
                        />
                        <Option
                          sx={seriesDayOptionStyles}
                          aria-label="Wednesday"
                          mr={1}
                          label="Wed"
                          data-testid="wednesday"
                          active={days.wednesday}
                          aria-pressed={days.wednesday}
                          onClick={() => {
                            setDays((prevState) => ({
                              ...prevState,
                              wednesday: !prevState.wednesday,
                            }));
                            setValue('tripTemplateAttributes.wednesday', !days.wednesday);
                          }}
                        />
                        <Option
                          sx={seriesDayOptionStyles}
                          aria-label="Thursday"
                          mr={1}
                          label="Thu"
                          data-testid="thursday"
                          active={days.thursday}
                          aria-pressed={days.thursday}
                          onClick={() => {
                            setDays((prevState) => ({
                              ...prevState,
                              thursday: !prevState.thursday,
                            }));
                            setValue('tripTemplateAttributes.thursday', !days.thursday);
                          }}
                        />
                        <Option
                          sx={seriesDayOptionStyles}
                          aria-label="Friday"
                          mr={1}
                          label="Fri"
                          data-testid="friday"
                          active={days.friday}
                          aria-pressed={days.friday}
                          onClick={() => {
                            setDays((prevState) => ({
                              ...prevState,
                              friday: !prevState.friday,
                            }));
                            setValue('tripTemplateAttributes.friday', !days.friday);
                          }}
                        />
                        <Option
                          sx={seriesDayOptionStyles}
                          aria-label="Saturday"
                          mr={1}
                          label="Sat"
                          data-testid="saturday"
                          active={days.saturday}
                          aria-pressed={days.saturday}
                          onClick={() => {
                            setDays((prevState) => ({
                              ...prevState,
                              saturday: !prevState.saturday,
                            }));
                            setValue('tripTemplateAttributes.saturday', !days.saturday);
                          }}
                        />
                        <Option
                          sx={seriesDayOptionStyles}
                          aria-label="Sunday"
                          mr={1}
                          label="Sun"
                          data-testid="sunday"
                          active={days.sunday}
                          aria-pressed={days.sunday}
                          onClick={() => {
                            setDays((prevState) => ({
                              ...prevState,
                              sunday: !prevState.sunday,
                            }));
                            setValue('tripTemplateAttributes.sunday', !days.sunday);
                          }}
                        />
                      </Box>
                      {errors.tripTemplateAttributes?.repeats && (
                        <FormHelperText data-testid="repeatsError" error>
                          {errors.tripTemplateAttributes?.repeats?.message}
                        </FormHelperText>
                      )}
                      <input
                        type="hidden"
                        data-testid="monday-input"
                        {...register('tripTemplateAttributes.monday', { value: days.monday })}
                      />
                      <input
                        type="hidden"
                        data-testid="tuesday-input"
                        {...register('tripTemplateAttributes.tuesday', { value: days.tuesday })}
                      />
                      <input
                        type="hidden"
                        data-testid="wednesday-input"
                        {...register('tripTemplateAttributes.wednesday', { value: days.wednesday })}
                      />
                      <input
                        type="hidden"
                        data-testid="thursday-input"
                        {...register('tripTemplateAttributes.thursday', { value: days.thursday })}
                      />
                      <input
                        type="hidden"
                        data-testid="friday-input"
                        {...register('tripTemplateAttributes.friday', { value: days.friday })}
                      />
                      <input
                        type="hidden"
                        data-testid="saturday-input"
                        {...register('tripTemplateAttributes.saturday', { value: days.saturday })}
                      />
                      <input
                        type="hidden"
                        data-testid="sunday-input"
                        {...register('tripTemplateAttributes.sunday', { value: days.sunday })}
                      />
                    </Box>
                  }
                />
                <ContentLabelField
                  id="series-date-range"
                  title="Dates"
                  editMode={true}
                  editView={
                    <Box maxWidth="390px">
                      <DateRangePicker
                        autoFocus={false}
                        onOpen={() => {
                          if (!startDateDisabled || !keyboardOpenRequested.current) {
                            return;
                          }

                          window.setTimeout(() => {
                            const focusableDay = document.querySelector<HTMLElement>(
                              '[role="dialog"] [role="gridcell"]:not([disabled]):not(.Mui-disabled)'
                            );

                            focusableDay?.focus();
                            keyboardOpenRequested.current = false;
                          });
                        }}
                        onClose={() => {
                          keyboardOpenRequested.current = false;
                        }}
                        value={[startDateField.value ?? null, endDateField.value ?? null]}
                        slots={{ textField: SeriesDateRangeTextField }}
                        rangePosition={startDateDisabled ? 'end' : rangePosition}
                        onRangePositionChange={setRangePosition}
                        disableDragEditing={startDateDisabled}
                        minDate={
                          startDateDisabled
                            ? rideDetailsDefault.tripTemplateAttributes?.startDate ?? nextAvailableDate
                            : nextAvailableDate
                        }
                        shouldDisableDate={(date, position) => {
                          if (position === 'end') {
                            return isBefore(startOfDay(date), startOfDay(minEndDate));
                          }

                          if (startDateDisabled) {
                            return !isSameDay(date, rideDetailsDefault.tripTemplateAttributes?.startDate ?? date);
                          }

                          return (
                            !!endDateField.value &&
                            isValid(endDateField.value) &&
                            isBefore(startOfDay(subDays(endDateField.value, 1)), startOfDay(date))
                          );
                        }}
                        onChange={([startDate, endDate]) => {
                          if (!startDateDisabled) {
                            startDateField.onChange(startDate);
                          }
                          // Range selection can clear an end date when moving the start past it.
                          // Keep the existing end for the form's normal validation instead.
                          const startChanged =
                            startDate === null || !startDateField.value
                              ? startDate !== startDateField.value
                              : !isEqual(startDate, startDateField.value);

                          endDateField.onChange(startChanged && endDate === null ? endDateField.value : endDate);
                        }}
                        slotProps={{
                          textField: dateRangeTextFieldProps,
                          openPickerButton: {
                            onKeyDown: (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                keyboardOpenRequested.current = true;
                              }
                            },
                          },
                        }}
                      />
                      <input type="hidden" name="anchorTime" />
                    </Box>
                  }
                />
              </>
              <ContentLabelField
                id="calendar"
                title="Calendar"
                editMode={true}
                editView={
                  <Controller
                    control={control}
                    name="calendarSlug"
                    defaultValue={rideDetailsDefault.calendarSlug}
                    render={({ field: { onChange, value } }) => (
                      <Box display="flex" gap={'14px'} flexGrow={1} alignItems="center">
                        {isLocationSelected ? (
                          <CalendarDropdown
                            onChange={onChange}
                            value={value}
                            error={!!errors.calendarSlug}
                            helperText={errors.calendarSlug?.message}
                          />
                        ) : (
                          <Text variant="body1" textColor="secondaryText" fontWeight={400} data-testid="helper-text">
                            <i>You can associate this series with a calendar once the anchor location is selected</i>
                          </Text>
                        )}
                      </Box>
                    )}
                  />
                }
              />
            </Box>
          </ContentList>
        </Grid>
      </Grid>
    </DateSectionContainer>
  );
};
